import { createContext, useCallback, useContext } from 'react';
import { CampaignForm } from '../hooks/useCampaignFormState';
import { CampaignFormValues } from '../types';

const CampaignFormContext = createContext<CampaignForm | null>(null);

export const CampaignFormProvider = CampaignFormContext.Provider;

export function useCampaignFormContext(): CampaignForm {
  const form = useContext(CampaignFormContext);
  if (!form) throw new Error('useCampaignFormContext must be used inside a CampaignFormProvider');
  return form;
}

/**
 * Everything one input needs: its value, its error, and how to change it.
 *
 * `onChange` and `onBlur` keep the same identity across renders. They end up in
 * dependency arrays — a fresh arrow each render silently re-runs the effect that
 * holds it, which once turned the calendar picker into a request loop.
 */
export function useField<K extends keyof CampaignFormValues>(name: K) {
  const { values, errors, setField, blur } = useCampaignFormContext();

  const onChange = useCallback(
    (value: CampaignFormValues[K]) => setField(name, value),
    [setField, name],
  );
  const onBlur = useCallback(() => blur(name as string), [blur, name]);

  return { value: values[name], error: errors[name as string], onChange, onBlur };
}

export { CampaignFormContext };
